// Módulo Convocatorias de Examen DGT · pestaña de ficha del alumno.
// -----------------------------------------------------------------------------
// Muestra:
//   · Panel superior con estado del expediente (APTO/ACTIVO + suspensos + fecha permitida).
//   · Botón "Nueva convocatoria" → abre wizard 3 pasos.
//   · Tarjetas de convocatorias (activas primero) + histórico timeline.
//   · Registro de resultado en un dialog reutilizando ExamResultDialog-like.
// -----------------------------------------------------------------------------
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Trophy,
  Plus,
  CalendarDays,
  Clock,
  MapPin,
  Send,
  Copy,
  Check,
  Ban,
  CheckCircle2,
  XCircle,
  UserX,
  Loader2,
  ExternalLink,
  AlertTriangle,
} from "lucide-react";
import ConvocatoriaWizard from "./ConvocatoriaWizard";
import StudentTasasBlock from "./StudentTasasBlock";

const STATUS_MAP = {
  programado:  { label: "Programado", cls: "border-blue-300 bg-blue-50 text-blue-800" },
  asistio:     { label: "Asistió",    cls: "border-amber-300 bg-amber-50 text-amber-800" },
  no_asistio:  { label: "No asistió", cls: "border-neutral-300 bg-neutral-100 text-neutral-700" },
  apto:        { label: "APTO",       cls: "border-emerald-400 bg-emerald-50 text-emerald-800" },
  no_apto:     { label: "NO APTO",    cls: "border-rose-300 bg-rose-50 text-rose-800" },
  anulado:     { label: "Anulado",    cls: "border-neutral-300 bg-neutral-50 text-neutral-500 line-through" },
};

const RESULT_OPTIONS = [
  { key: "apto",         label: "APTO",                Icon: CheckCircle2, cls: "border-emerald-400 hover:bg-emerald-50 text-emerald-800" },
  { key: "no_apto",      label: "NO APTO",             Icon: XCircle,      cls: "border-rose-400 hover:bg-rose-50 text-rose-800" },
  { key: "no_presentado",label: "No presentado",       Icon: UserX,        cls: "border-amber-400 hover:bg-amber-50 text-amber-800" },
  { key: "anulado_dgt",  label: "Convocatoria anulada",Icon: Ban,          cls: "border-neutral-400 hover:bg-neutral-50 text-neutral-700" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""));
    return d.toLocaleDateString("es-ES", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.programado;
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${s.cls}`}>{s.label}</span>;
}

// -----------------------------------------------------------------------------
// Diálogo interno de resultado (aislado, no depende de Dashboard)
// -----------------------------------------------------------------------------
function ResultInlineDialog({ open, conv, onClose, onSaved }) {
  const [choice, setChoice] = useState("");
  const [justified, setJustified] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (!open) { setChoice(""); setJustified(false); } }, [open]);

  if (!open || !conv) return null;

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/convocatorias/${conv.id}/result`, {
        result: choice,
        justified: choice === "no_presentado" ? justified : false,
      });
      if (data?.aprobado) toast.success("✅ APTO obtenido · expediente cerrado");
      else if (choice === "no_apto") toast.info(`Suspenso registrado. Próximo examen: ${data?.student_updates?.next_exam_allowed_from || "sin restricción"}`);
      else toast.success("Resultado registrado");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo guardar el resultado");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="convocatoria-result-dialog">
        <h3 className="text-lg font-bold flex items-center gap-2">
          <Trophy className="h-5 w-5 text-orange-600" />
          Resultado · Examen {conv.kind === "practico" ? "PRÁCTICO" : "TEÓRICO"}
        </h3>
        <p className="text-xs text-neutral-500 mt-0.5">{fmtDate(conv.date)} · {conv.time}</p>

        <div className="grid grid-cols-2 gap-2 mt-4">
          {RESULT_OPTIONS.map(({ key, label, Icon, cls }) => (
            <button key={key} type="button" onClick={() => setChoice(key)}
              data-testid={`conv-result-${key}`}
              className={`flex items-center justify-center gap-2 rounded-xl border-2 px-3 py-3 text-sm font-semibold transition ${cls} ${
                choice === key ? "ring-2 ring-orange-500 bg-orange-50" : ""
              }`}>
              <Icon className="h-4 w-4" />{label}
            </button>
          ))}
        </div>

        {choice === "no_presentado" && (
          <label className="mt-3 flex items-start gap-2 text-sm p-3 rounded-lg bg-amber-50 border border-amber-200">
            <input type="checkbox" checked={justified} onChange={(e) => setJustified(e.target.checked)}
              data-testid="conv-result-justified" className="mt-0.5" />
            <span><b>Justificado</b> · No consumirá convocatoria.</span>
          </label>
        )}

        {choice === "apto" && conv.kind === "practico" && (
          <div className="mt-3 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg p-2 font-semibold">
            Al confirmar, el alumno pasará a estado APTO y su expediente quedará cerrado en verde.
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm rounded-lg border border-neutral-300 hover:bg-neutral-50">Cancelar</button>
          <button type="button" onClick={submit} disabled={!choice || submitting}
            data-testid="conv-result-confirm"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-60">
            {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Guardando…</> : "Registrar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Tarjeta de convocatoria
// -----------------------------------------------------------------------------
function ConvocatoriaCard({ conv, onSendWhatsApp, onOpenResult, onCancel }) {
  const kindLabel = conv.kind === "practico" ? "PRÁCTICO" : "TEÓRICO";
  const isDone = ["apto", "no_apto", "anulado"].includes(conv.status);
  return (
    <div className={`rounded-xl border-2 bg-white p-4 shadow-sm ${
      conv.status === "apto"    ? "border-emerald-400 bg-emerald-50/40" :
      conv.status === "no_apto" ? "border-rose-300 bg-rose-50/30" :
      conv.status === "anulado" ? "border-neutral-300 bg-neutral-50 opacity-70" :
      "border-blue-200"
    }`} data-testid={`convocatoria-card-${conv.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Trophy className="h-4 w-4 text-orange-600 shrink-0" />
            <span className="font-bold text-sm">Examen {kindLabel}</span>
            <StatusBadge status={conv.status} />
          </div>
          <div className="text-sm text-neutral-700 flex items-center gap-2 mt-1">
            <CalendarDays className="h-3.5 w-3.5 text-neutral-400" /> {fmtDate(conv.date)}
            <Clock className="h-3.5 w-3.5 text-neutral-400 ml-2" /> {conv.time}
          </div>
          <div className="text-[13px] text-neutral-600 flex items-start gap-1.5 mt-1">
            <MapPin className="h-3.5 w-3.5 text-neutral-400 mt-0.5 shrink-0" />
            <span className="truncate">{conv.center_snapshot?.name || "—"}</span>
          </div>
          {conv.kind === "practico" && conv.meeting_snapshot?.name && (
            <div className="text-[12px] text-neutral-500 ml-5 mt-0.5">
              Punto encuentro: {conv.meeting_snapshot.name}
              {conv.hora_punto_encuentro && ` · ${conv.hora_punto_encuentro}`}
            </div>
          )}
          {conv.whatsapp_sent_at && (
            <div className="text-[11px] text-emerald-700 flex items-center gap-1 mt-1">
              <Check className="h-3 w-3" /> WhatsApp enviado {fmtDate(conv.whatsapp_sent_at.slice(0, 10))}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        <button type="button" onClick={() => onSendWhatsApp(conv)}
          data-testid={`conv-btn-whatsapp-${conv.id}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-emerald-600 hover:bg-emerald-700 text-white">
          <Send className="h-3.5 w-3.5" /> {conv.whatsapp_sent_at ? "Reenviar WhatsApp" : "Enviar WhatsApp"}
        </button>
        {!isDone && (
          <button type="button" onClick={() => onOpenResult(conv)}
            data-testid={`conv-btn-result-${conv.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-orange-300 bg-orange-50 hover:bg-orange-100 text-orange-800">
            <Trophy className="h-3.5 w-3.5" /> Registrar resultado
          </button>
        )}
        {!isDone && (
          <button type="button" onClick={() => onCancel(conv)}
            data-testid={`conv-btn-cancel-${conv.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-neutral-300 hover:bg-neutral-50 text-neutral-700">
            <Ban className="h-3.5 w-3.5" /> Anular
          </button>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Panel WhatsApp
// -----------------------------------------------------------------------------
function WhatsAppSheet({ open, data, onClose, onMarkSent }) {
  const [text, setText] = useState(data?.message || "");
  useEffect(() => { setText(data?.message || ""); }, [data]);
  if (!open || !data) return null;

  const openWa = () => {
    // Reconstruye la URL con el texto editado
    const clean = (data.phone || "").replace(/[^\d+]/g, "").replace(/^\+/, "");
    const url = `https://wa.me/${clean}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    onMarkSent("opened", text);
  };
  const copyMsg = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Mensaje copiado al portapapeles");
      onMarkSent("copied", text);
    } catch { toast.error("No se pudo copiar"); }
  };
  const markSent = () => { onMarkSent("manual", text); toast.success("Marcado como enviado"); };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="convocatoria-whatsapp-dialog">
        <h3 className="text-lg font-bold flex items-center gap-2">
          <Send className="h-5 w-5 text-emerald-600" /> Enviar convocatoria por WhatsApp
        </h3>
        <p className="text-xs text-neutral-500 mt-0.5">Puedes editar el mensaje antes de enviarlo.</p>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={16}
          data-testid="convocatoria-whatsapp-textarea"
          className="mt-3 w-full font-mono text-[12px] p-3 border border-neutral-300 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none"
        />

        <div className="text-[11px] text-neutral-500 mt-2">
          Teléfono: <code className="font-mono">{data.phone || "sin teléfono"}</code>
        </div>

        <div className="flex flex-wrap justify-end gap-2 mt-3">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm rounded-lg border border-neutral-300 hover:bg-neutral-50">Cerrar</button>
          <button type="button" onClick={markSent} data-testid="whatsapp-mark-sent"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border border-neutral-300 hover:bg-neutral-50">
            <Check className="h-3.5 w-3.5" /> Marcar como enviado
          </button>
          <button type="button" onClick={copyMsg} data-testid="whatsapp-copy"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border border-neutral-300 hover:bg-neutral-50">
            <Copy className="h-3.5 w-3.5" /> Copiar mensaje
          </button>
          <button type="button" onClick={openWa} data-testid="whatsapp-open"
            disabled={!data.phone}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60">
            <ExternalLink className="h-3.5 w-3.5" /> Abrir en WhatsApp Web
          </button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Panel principal
// -----------------------------------------------------------------------------
export default function ExamenesDgtTab({ student, onReload }) {
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [resultOpen, setResultOpen] = useState({ open: false, conv: null });
  const [waOpen, setWaOpen] = useState({ open: false, data: null, convId: "" });

  const load = useCallback(async () => {
    if (!student?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/students/${student.id}/convocatorias`);
      setConvs(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Error cargando convocatorias");
    } finally {
      setLoading(false);
    }
  }, [student?.id]);

  useEffect(() => { load(); }, [load]);

  const openWhatsApp = async (conv) => {
    try {
      const { data } = await api.post(`/convocatorias/${conv.id}/whatsapp`, { mode: "opened", message: "" });
      setWaOpen({ open: true, data, convId: conv.id });
      // Nota: NO cerramos aquí; el usuario elige acción en el sheet.
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Error preparando WhatsApp");
    }
  };
  const markSent = async (mode, message) => {
    if (!waOpen.convId) return;
    try {
      await api.post(`/convocatorias/${waOpen.convId}/whatsapp`, { mode, message });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo registrar el envío");
    } finally {
      await load();
    }
  };

  const cancelConv = async (conv) => {
    if (!window.confirm(`¿Anular la convocatoria del ${conv.date}?`)) return;
    try {
      await api.post(`/convocatorias/${conv.id}/cancel`);
      toast.success("Convocatoria anulada");
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo anular");
    }
  };

  const activeConvs = useMemo(() => convs.filter((c) => c.status === "programado" || c.status === "asistio"), [convs]);
  const historyConvs = useMemo(() => convs.filter((c) => !(c.status === "programado" || c.status === "asistio")), [convs]);

  const isApto = !!student?.apto_date;
  const suspensos = Number(student?.suspensos_count || 0);
  const nextAllowed = student?.next_exam_allowed_from || "";

  return (
    <div className="space-y-4" data-testid="examenes-dgt-tab">
      {/* Estado del alumno */}
      <div className={`rounded-xl border-2 p-4 ${
        isApto ? "border-emerald-400 bg-emerald-50" : "border-blue-200 bg-white"
      }`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Trophy className={`h-5 w-5 ${isApto ? "text-emerald-600" : "text-orange-600"}`} />
              <h3 className="font-bold text-base">Estado del expediente</h3>
            </div>
            {isApto ? (
              <p className="text-emerald-900 font-bold text-lg mt-1" data-testid="student-apto-banner">
                ✅ APTO obtenido el {fmtDate(student.apto_date)}
              </p>
            ) : (
              <p className="text-sm text-neutral-700 mt-1">
                Alumno <span className="font-bold text-blue-700">ACTIVO</span>
                {suspensos > 0 && <> · Suspensos acumulados: <span className="font-mono font-bold">{suspensos}</span></>}
              </p>
            )}
            {!isApto && student?.theory_history?.approved_date && (
              <p className="text-sm text-emerald-800 mt-1" data-testid="theory-history-badge">
                📘 Teórico aprobado (registro histórico) el <b>{fmtDate(student.theory_history.approved_date)}</b>
                {student.theory_history.center_name && <> · {student.theory_history.center_name}</>}
              </p>
            )}
            {!isApto && nextAllowed && (
              <p className="text-sm text-amber-800 mt-1 flex items-center gap-1.5">
                <AlertTriangle className="h-4 w-4" />
                Próximo examen permitido a partir del <b>{fmtDate(nextAllowed)}</b>
              </p>
            )}
          </div>
          <button type="button" onClick={() => setWizardOpen(true)} disabled={isApto}
            data-testid="convocatoria-new-btn"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50">
            <Plus className="h-4 w-4" /> Nueva convocatoria
          </button>
        </div>
      </div>

      {/* V1.51 · Tasas DGT del alumno (consumo visual) */}
      <StudentTasasBlock student={student} />

      {loading ? (
        <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-neutral-400" /></div>
      ) : (
        <>
          {/* Activas */}
          <div>
            <h4 className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold mb-2">
              Próximas convocatorias ({activeConvs.length})
            </h4>
            {activeConvs.length === 0 ? (
              <div className="rounded-lg border border-dashed border-neutral-300 py-6 text-center text-sm text-neutral-500">
                No hay convocatorias programadas.
              </div>
            ) : (
              <div className="space-y-3">
                {activeConvs.map((c) => (
                  <ConvocatoriaCard
                    key={c.id} conv={c}
                    onSendWhatsApp={openWhatsApp}
                    onOpenResult={(v) => setResultOpen({ open: true, conv: v })}
                    onCancel={cancelConv}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Histórico */}
          {historyConvs.length > 0 && (
            <div>
              <h4 className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold mb-2 mt-4">
                Histórico ({historyConvs.length})
              </h4>
              <div className="space-y-3 opacity-90">
                {historyConvs.map((c) => (
                  <ConvocatoriaCard
                    key={c.id} conv={c}
                    onSendWhatsApp={openWhatsApp}
                    onOpenResult={(v) => setResultOpen({ open: true, conv: v })}
                    onCancel={cancelConv}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Wizard */}
      <ConvocatoriaWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        student={student}
        onCreated={async () => { setWizardOpen(false); await load(); onReload?.(); }}
      />

      {/* Result */}
      <ResultInlineDialog
        open={resultOpen.open}
        conv={resultOpen.conv}
        onClose={() => setResultOpen({ open: false, conv: null })}
        onSaved={async () => { await load(); onReload?.(); }}
      />

      {/* WhatsApp */}
      <WhatsAppSheet
        open={waOpen.open}
        data={waOpen.data}
        onClose={() => setWaOpen({ open: false, data: null, convId: "" })}
        onMarkSent={markSent}
      />
    </div>
  );
}
